<script lang="ts">
  import About from "$routes/about.svelte"
  import Home from "$routes/home.svelte"
  import { Router, goto, type RouteConfig } from "@mateothegreat/svelte5-router"
  import { myDefaultRouterConfig } from "$lib/default-route-config"
  import Library from "$routes/media/library/library.svelte"
  import {
    authState,
    getIdentity,
    logout,
    validateSession as ensureSession,
    primeServerTokens,
  } from '$lib/auth.svelte'
  import * as DropdownMenu from "$lib/components/ui/dropdown-menu/index.js";

  import favicon from '$lib/assets/icons/favicon.svg'
  import overlay from '$lib/assets/overlay.png'
  import bars from '$lib/assets/icons/bars.svg'
  import Icon from '$lib/components/nuread/Icon.svelte'

  import { ModeWatcher, toggleMode } from 'mode-watcher'
  import SunIcon from '@lucide/svelte/icons/sun'
  import MoonIcon from '@lucide/svelte/icons/moon'
  import UserIcon from '@lucide/svelte/icons/user'
  import LogOutIcon from '@lucide/svelte/icons/log-out'

  import { Button } from '$lib/components/ui/button/index'
  import * as Avatar from '$lib/components/ui/avatar/index'
  import * as Sidebar from '$lib/components/ui/sidebar/index'
  import AppSidebar from '$lib/components/nuread/app-sidebar.svelte'
  import NavBreadcrumb from '$lib/components/nuread/NavBreadcrumb.svelte'
  import Login from "$routes/login.svelte"
  import { getServers } from "$lib/apis/server/api"
  import { refreshServerAccess } from '$lib/server-access.svelte'
  import Server from "$routes/media/server.svelte"
  import { getLibraries } from "$lib/apis/library/api"
  import Series from "$routes/media/library/series/series.svelte"
  import Reader from "$routes/media/library/series/reader.svelte"

  import { get } from 'svelte/store'
  import { servers as serversStore, currentPath, readerHeaderVisibleStore } from '$lib/store'
  import Profile from "$routes/profile.svelte"
  import ServerSettings from "$routes/media/server-settings.svelte"
  import LibrarySettings from "$routes/media/library/library-settings.svelte"
  import ToastStack from '$lib/components/nuread/ToastStack.svelte'
  import GlobalSearch from '$lib/components/nuread/GlobalSearch.svelte'
  import ClaimServer from '$lib/components/nuread/modals/ClaimServer.svelte'
  import GrantAccess from '$lib/components/nuread/modals/GrantAccess.svelte'
  import { claimServerModal } from '$lib/claim-server.svelte'
  import { grantAccessModal } from '$lib/grant-access.svelte'
  import { loadPreferences, resetPreferences } from '$lib/preferences.svelte'
  import ServerIcon from '@lucide/svelte/icons/server'

  async function validateSession() {
    if (await ensureSession()) {
      loadPreferences()
      return true
    }
     goto("/login");
    return false
  }

  function mapServers(servers: any[]) {
    let serverMap = {}
    servers.forEach(s => serverMap[s.uid]=s )
    return serverMap
  }

  let serverRes = $state({})
  let libraries = $state<Record<string, any[]>>({})
  let disconnectedServers = $state<Record<string, boolean>>({})
  const libraryTimeouts = new Map<string, ReturnType<typeof setTimeout>>()
  const LIBRARY_LOAD_TIMEOUT_MS = 15_000

  function clearLibraryTimeout(uid: string) {
    const t = libraryTimeouts.get(uid)
    if (t) {
      clearTimeout(t)
      libraryTimeouts.delete(uid)
    }
  }

  function armLibraryTimeout(uid: string) {
    clearLibraryTimeout(uid)
    libraryTimeouts.set(
      uid,
      setTimeout(() => {
        libraryTimeouts.delete(uid)
        if (libraries[uid] === undefined) {
          disconnectedServers[uid] = true
        }
      }, LIBRARY_LOAD_TIMEOUT_MS),
    )
  }

  async function loadServerLibraries(server: any) {
    const uid = server.uid as string
    armLibraryTimeout(uid)
    try {
      const r = await getLibraries(server)
      clearLibraryTimeout(uid)
      libraries[uid] = r?.Libraries ?? []
      disconnectedServers[uid] = false
    } catch (err) {
      console.error('load libraries failed', err)
      // Leave libraries unset; the timeout will mark disconnected if nothing arrives.
    }
  }

  async function refreshServers() {
    serverRes = (await getServers()) ?? {}
  }

  async function onServerClaimed() {
    await refreshServers()
    await getIdentity()
    await refreshServerAccess()
  }

  void refreshServers()

  const servers: any[] = $derived(serverRes?.servers ?? []);

  async function refreshLibraries(serverUid: string) {
    const server = servers.find((s) => s.uid === serverUid)
    if (!server) return
    await loadServerLibraries(server)
  }

  // $inspect("SERVERS=", mapServers(servers) )

  $effect(() => {
    serversStore.set(mapServers(servers))
    const uids = new Set(servers.map((s: { uid: string }) => s.uid))
    for (const uid of [...libraryTimeouts.keys()]) {
      if (!uids.has(uid)) clearLibraryTimeout(uid)
    }
    // Batch-mint per-server access tokens once, then build the sidebar.
    primeServerTokens(servers.map((s: { uid: string }) => s.uid)).finally(() => {
      servers.forEach((server: { uid: string }) => {
        void loadServerLibraries(server)
      })
    })
  })

  let maxWidth: number = $state(0)
  let sidebarWidth: number = $state(0)

  let sidebar: boolean = $state(window.innerWidth < 740 ? false : true)
  function toggleSidebar() {
    sidebar = !sidebar
  }

  function getSvgPath(svg: string) {
    const raw = decodeURIComponent(svg.toString())
    const match = raw.match(/\bd=["']([^"']+)["']/)
    if (match) return match[1].replaceAll('%20', ' ')
    return raw
      .split('d=')[1]
      ?.split(/\/?%3e|>/i)[0]
      ?.replaceAll('%20', ' ')
      ?.replaceAll(/['"]/g, '')
      ?.trim() ?? ''
  }

  const UUID_REGEX = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}"

  const routes: RouteConfig[] = [
    {
      path: "/",
      component: Home,
      hooks: {
        pre: [validateSession],
      },
    },
    {
      path: "login",
      component: Login
    },
    {
      path: "about",
      component: About
    },
    {
      path: "profile",
      component: Profile,
      hooks: {
        pre: [validateSession],
      },
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})$`,
      component: Server,
      hooks: {
        pre: [validateSession],
      },
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})/settings$`,
      component: ServerSettings,
      hooks: {
        pre: [validateSession],
      },
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})/library/(?<libraryid>${UUID_REGEX})$`,
      component: Library
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})/library/(?<libraryid>${UUID_REGEX})/settings$`,
      component: LibrarySettings,
      hooks: {
        pre: [validateSession],
      },
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})/library/(?<libraryid>${UUID_REGEX})/series/(?<seriesid>${UUID_REGEX})$`,
      component: Series
    },
    {
      path: `/media/(?<mediaid>${UUID_REGEX})/library/(?<libraryid>${UUID_REGEX})/series/(?<seriesid>${UUID_REGEX})/(?<type>chapter|volume)/(?<num>[0-9]+)\.?(?<subNum>[0-9]+)?$`,
      component: Reader
    },
  ];

  const isReaderPath = $derived(
    $currentPath.includes("/volume/") || $currentPath.includes("/chapter/")
  )
  const isLoginPath = $derived(
    $currentPath === '/login' || $currentPath.endsWith('/login')
  )
  const isServerHomePath = $derived(
    new RegExp(`^/media/${UUID_REGEX}/?$`).test($currentPath)
  )

  const displayName = $derived(
    authState.name && authState.name !== 'Not Connected'
      ? authState.name
      : 'Reader'
  )
  const displayEmail = $derived(authState.email || '')
  const avatarInitials = $derived(
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('') || '?'
  )

  $effect(() => {
    if (isLoginPath) return
    void getIdentity()
  })

  async function handleLogout() {
    try {
      await logout()
    } finally {
      resetPreferences()
      goto('/login')
    }
  }

  let readerHeaderVisible = $state(get(readerHeaderVisibleStore))
  $effect(() => {
    const unsub = readerHeaderVisibleStore.subscribe((v) => {
      readerHeaderVisible = v
    })
    return unsub
  })

  $effect(() => {
    if (!isReaderPath) readerHeaderVisibleStore.set(true)
  })

  // SPA navigations keep window scroll; reset so views feel freshly loaded.
  $effect(() => {
    void $currentPath
    window.scrollTo(0, 0)
  })

  let wasReaderPath = $state(false)
  $effect(() => {
    if (isReaderPath && !wasReaderPath) {
      sidebar = false
    }
    wasReaderPath = isReaderPath
  })

  $effect(() => {
    if (!isReaderPath) return
    if (!readerHeaderVisible) sidebar = false
  })

</script>

<svelte:head><link rel="icon" href={favicon} /></svelte:head>

<div class="w-full h-0" bind:clientWidth={null, v => (maxWidth = v!)}></div>
{#if !isLoginPath}
  <div
    class="fixed h-full w-full -z-1 blur-[1px] dark:brightness-40 opacity-40 dark:opacity-40 bg-cover"
    style="background-image: url('{overlay}')"
  />
{/if}

<ModeWatcher />

{#if !isLoginPath}
<div
  id="header"
  class="fixed top-0 bg-border inset-x-0 h-14 mx-2 mt-2 z-1 rounded z-999"
  style="opacity: {isReaderPath && !readerHeaderVisible ? 0 : 1}; transform: translateY({isReaderPath && !readerHeaderVisible ? '-100%' : '0'}); transition: opacity 0.25s ease, transform 0.25s ease;"
>
  <div class="absolute flex row mx-7">
    <Button class="my-3" variant="ghost" onclick={() => toggleSidebar()}>
      <Icon viewBox="0 0 640 640" class="size-7">
        <path d={getSvgPath(bars)} />
      </Icon>
    </Button>
    <Button class="my-3" variant="ghost" onclick={() => {goto("/")}}>
      <Icon viewBox="0 0 640 512" class="size-10 text-emerald-600">
        <path fill-rule="evenodd" d={getSvgPath(favicon)} />
      </Icon>
      <h1 class="ml-2 my-3 text-lg font-semibold">nuread</h1>
    </Button>
  </div>
  <GlobalSearch {libraries} />
  <DropdownMenu.Root>
    <DropdownMenu.Trigger>
      {#snippet child({ props })}
        <button
          {...props}
          class="absolute right-16 rounded-full outline-none ring-offset-2
            focus-visible:ring-2 focus-visible:ring-latte-green
            dark:focus-visible:ring-macchiato-green"
          aria-label="Account menu"
        >
          <Avatar.Root
            class="my-2 h-10 w-10 ring-1 ring-latte-green/70
              dark:ring-macchiato-green/70"
          >
            <Avatar.Fallback
              class="bg-latte-mantle text-sm font-semibold text-latte-green
                dark:bg-macchiato-mantle dark:text-macchiato-green"
            >
              {avatarInitials}
            </Avatar.Fallback>
          </Avatar.Root>
        </button>
      {/snippet}
    </DropdownMenu.Trigger>
    <DropdownMenu.Content
      align="end"
      class="w-64 border-latte-surface1/60 bg-latte-mantle/95 p-1.5 shadow-lg
        backdrop-blur-md dark:border-macchiato-surface1/60 dark:bg-macchiato-mantle/95"
    >
      <DropdownMenu.Label class="px-2.5 py-2.5 font-normal">
        <div class="flex items-center gap-3">
          <Avatar.Root
            class="h-9 w-9 shrink-0 ring-1 ring-latte-green/60
              dark:ring-macchiato-green/60"
          >
            <Avatar.Fallback
              class="bg-latte-base text-xs font-semibold text-latte-green
                dark:bg-macchiato-base dark:text-macchiato-green"
            >
              {avatarInitials}
            </Avatar.Fallback>
          </Avatar.Root>
          <div class="min-w-0 flex flex-col gap-0.5">
            <span
              class="truncate text-sm font-semibold text-latte-text
                dark:text-macchiato-text"
            >
              {displayName}
            </span>
            {#if displayEmail}
              <span
                class="truncate text-xs font-light text-latte-text/60
                  dark:text-macchiato-text/60"
              >
                {displayEmail}
              </span>
            {/if}
          </div>
        </div>
      </DropdownMenu.Label>
      <DropdownMenu.Separator
        class="bg-latte-surface1/70 dark:bg-macchiato-surface1/70"
      />
      <DropdownMenu.Group>
        <DropdownMenu.Item
          class="gap-2 rounded-sm"
          onclick={() => goto('/profile')}
        >
          <UserIcon class="size-4" strokeWidth={1.75} />
          Profile
        </DropdownMenu.Item>
        <DropdownMenu.Item
          class="gap-2 rounded-sm"
          onclick={() => (claimServerModal.open = true)}
        >
          <ServerIcon class="size-4" strokeWidth={1.75} />
          Claim Server
        </DropdownMenu.Item>
        <DropdownMenu.Item
          variant="destructive"
          class="gap-2 rounded-sm"
          onclick={handleLogout}
        >
          <LogOutIcon class="size-4" strokeWidth={1.75} />
          Sign out
        </DropdownMenu.Item>
      </DropdownMenu.Group>
    </DropdownMenu.Content>
  </DropdownMenu.Root>
  <Button
    onclick={toggleMode}
    variant="ghost"
    size="icon"
    class="absolute my-2.5 right-5"
  >
    <SunIcon
      class=" scale-100 rotate-0 !transition-all dark:scale-0 dark:-rotate-90"
    />
    <MoonIcon
      class="absolute scale-0 rotate-90 !transition-all dark:scale-100 dark:rotate-0"
    />
    <span class="sr-only">Toggle theme</span>
  </Button>
</div>
{/if}

{#if isLoginPath}
  <main class="relative min-h-dvh w-full">
    <Router
      {routes}
 {...myDefaultRouterConfig}
    />
  </main>
{:else}
<Sidebar.Provider bind:open={sidebar}>
  <AppSidebar
    onToggle={toggleSidebar}
    {servers}
    {libraries}
    {disconnectedServers}
    {refreshLibraries}
  />
  <main class="relative w-[100%]">
    <div class="{ isReaderPath ? 'h-0' : 'h-10'}"></div>
    {#if !isReaderPath && !isServerHomePath}
      <div class="absolute top-20 left-2 sm:left-4 z-[40] max-w-[min(100%,42rem)]">
        <NavBreadcrumb {servers} {libraries} />
      </div>
    {/if}
    <Router
      {routes}
 {...myDefaultRouterConfig}
/>
  </main>
</Sidebar.Provider>
{/if}
<ClaimServer bind:open={claimServerModal.open} onClaimed={onServerClaimed} />
<GrantAccess
  bind:open={grantAccessModal.open}
  serverUid={grantAccessModal.serverUid}
  serverName={grantAccessModal.serverName}
/>
<ToastStack />
